import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Spacing, IconText, Text, Icon, MessageBox } from '@reservamos/elements';
import CheckList from '../../../ui/atoms/CheckList';
import PaymentImage from '../../../ui/atoms/PaymentImage';

/**
 * ConektaStoreForm component.
 * Renders the OXXO payment form with instructions and information for cash payments at OXXO stores.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.isOpenTicket - Whether this is an open ticket purchase.
 * @returns {React.Component} A component that displays OXXO payment instructions.
 */
const ConektaStoreForm = ({ isOpenTicket }) => {
  const { env, features } = useSelector((state) => state.whitelabelConfig);
  const { SHOW_OXXO_CANCEL_CONDITIONS, OXXO_SEAT_RESERVATION_WARNING } = features;
  const { t } = useTranslation('purchase');

  const defaultLabels = [
    t('label.pay_in_oxxo'),
    t('text.you_will_receive_the_reference_number_oxxo'),
    t('text.we_send_an_email_when_paying'),
  ];

  const cancelConditions = [
    t('label.oxxo_cancel_condition_1'),
    t('label.oxxo_cancel_condition_2'),
    t('label.oxxo_cancel_condition_3'),
  ];

  // TODO: This needs to work for all tenants and it seems too specific
  const gfa = [...defaultLabels];
  if (!isOpenTicket) gfa.push(t('text.secure_seat_until_payment'));
  const rollbits = [...defaultLabels];
  if (!isOpenTicket) rollbits.push(t('text.secure_seat_until_payment'));

  const showOxxoSeatWarning = !isOpenTicket && OXXO_SEAT_RESERVATION_WARNING;
  const timeLimit = env?.conekta?.seatReservationTimeLimit ?? 20;

  const labelsType = {
    gfa,
    'roll-bits': rollbits,
    'default': defaultLabels,
  };

  return (
    <div className="checkout-section">
      <Spacing vertical>
        <PaymentImage type="oxxo-logo" />

        <CheckList
          title={t('label.pay_with_cash_in_oxxo')}
          labels={labelsType[env.brand] ?? labelsType.default}
          disclaimer={!SHOW_OXXO_CANCEL_CONDITIONS ? t('text.oxxo_commission') : ''}
        />

        {showOxxoSeatWarning && (
          <MessageBox borderColor="warning">
            <Text size="S">{t('text.oxxo_seat_reservation_warning', { timeLimit })}</Text>
          </MessageBox>
        )}

        {SHOW_OXXO_CANCEL_CONDITIONS && (
          <Spacing vertical>
            {Boolean(t('label.oxxo_cancel_condition_title')) && (
              <Text size="S" weight="semibold">
                {t('label.oxxo_cancel_condition_title')}
              </Text>
            )}
            <Spacing vertical size="S">
              {cancelConditions.map((label) => (
                <IconText
                  key={label}
                  label={label}
                  iconType="CheckmarkAccent"
                  fontSize="S"
                  iconSize="S"
                />
              ))}
              <Spacing key="oxxo_cancel_condition_4" size="XS" alignItems="baseline">
                <Icon type="CheckmarkAccent" size="S" color="accent" />
                <Text size="S">
                  <Trans
                    t={t}
                    i18nKey="label.oxxo_cancel_condition_4"
                    components={{
                      phone: <strong style={{ textDecoration: 'underline' }} />,
                    }}
                  />
                </Text>
              </Spacing>
            </Spacing>
            {Boolean(t('text.oxxo_commission')) && (
              <Text size="XS">{`** ${t('text.oxxo_commission')}`}</Text>
            )}
          </Spacing>
        )}
      </Spacing>
    </div>
  );
};

ConektaStoreForm.propTypes = {
  isOpenTicket: PropTypes.bool.isRequired,
};

export default ConektaStoreForm;
